import { useAtomRefresh, useAtomValue } from "@effect/atom-react";
import {
  executeAtomQuery,
  squashAtomCommandFailure,
} from "@afar-research/client-runtime/state/runtime";
import type {
  EnvironmentId,
  ProjectListEntriesResult,
  ProjectReadFileResult,
} from "@afar-research/contracts";
import * as Cause from "effect/Cause";
import * as Option from "effect/Option";
import { AsyncResult } from "effect/unstable/reactivity";
import { useCallback, useEffect, useRef, useState } from "react";

import { appAtomRegistry } from "~/rpc/atomRegistry";
import { projectEnvironment } from "~/state/projects";
import { useAtomCommand } from "~/state/use-atom-command";

const EMPTY_PROJECT_FILE_PATH = "";
function optimisticFileAtom(environmentId: EnvironmentId, cwd: string, relativePath: string) {
  return projectEnvironment.optimisticFile({ environmentId, cwd, relativePath });
}

// A draft belongs to the file, not the panel displaying it. Keep its atom
// mounted until a confirmed save or explicit Reload releases the draft.
const draftReleases = new Map<ReturnType<typeof optimisticFileAtom>, () => void>();

interface ProjectQueryState<A> {
  readonly data: A | null;
  readonly error: string | null;
  readonly isPending: boolean;
  readonly refresh: () => void;
}

function failureMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Workspace refresh failed. Please try again.";
}

export function getProjectEntriesQueryAtom(environmentId: EnvironmentId, cwd: string) {
  return projectEnvironment.listEntries({ environmentId, input: { cwd } });
}

export function getProjectFileQueryAtom(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string | null,
) {
  return projectEnvironment.readFile({
    environmentId,
    input: { cwd, relativePath: relativePath ?? EMPTY_PROJECT_FILE_PATH },
  });
}

export function setProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string,
  contents: string,
): void {
  const atom = optimisticFileAtom(environmentId, cwd, relativePath);
  if (!draftReleases.has(atom)) draftReleases.set(atom, appAtomRegistry.mount(atom));
  appAtomRegistry.set(atom, {
    confirmedAgainst: undefined,
    data: {
      relativePath,
      contents,
      byteLength: new TextEncoder().encode(contents).byteLength,
      truncated: false,
    },
  });
}

export function getOptimisticProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string,
): ProjectReadFileResult | null {
  return appAtomRegistry.get(optimisticFileAtom(environmentId, cwd, relativePath))?.data ?? null;
}

export function confirmProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string,
  contents: string,
): boolean {
  const atom = optimisticFileAtom(environmentId, cwd, relativePath);
  const optimisticFile = appAtomRegistry.get(atom);
  if (optimisticFile?.data.contents !== contents) return false;

  const queryAtom = getProjectFileQueryAtom(environmentId, cwd, relativePath);
  const confirmed = {
    ...optimisticFile,
    confirmedAgainst: appAtomRegistry.get(queryAtom),
  };
  appAtomRegistry.set(atom, confirmed);
  appAtomRegistry.refresh(queryAtom);
  void executeAtomQuery(appAtomRegistry, queryAtom, {
    reportDefect: false,
    reportFailure: false,
  }).then((result) => {
    if (result._tag === "Success" && appAtomRegistry.get(atom) === confirmed) {
      clearProjectFileQueryData(environmentId, cwd, relativePath);
    }
  });
  return true;
}

export function resolveProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string | null,
  data: ProjectReadFileResult | null,
): ProjectReadFileResult | null {
  if (relativePath === null) return data;
  return appAtomRegistry.get(optimisticFileAtom(environmentId, cwd, relativePath))?.data ?? data;
}

export function clearProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string,
): void {
  const atom = optimisticFileAtom(environmentId, cwd, relativePath);
  appAtomRegistry.set(atom, null);
  draftReleases.get(atom)?.();
  draftReleases.delete(atom);
}

/** Drops the editor's unsaved text for a file and reads it from disk again. */
export function reloadProjectFileQueryData(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string,
): void {
  clearProjectFileQueryData(environmentId, cwd, relativePath);
  appAtomRegistry.refresh(getProjectFileQueryAtom(environmentId, cwd, relativePath));
}

function errorMessage<A>(result: AsyncResult.AsyncResult<A, unknown>): string | null {
  if (result._tag !== "Failure") return null;
  const cause = Cause.squash(result.cause);
  return cause instanceof Error ? cause.message : "Workspace query failed.";
}

export function useProjectEntriesQuery(
  environmentId: EnvironmentId,
  cwd: string,
): ProjectQueryState<ProjectListEntriesResult> {
  const atom = getProjectEntriesQueryAtom(environmentId, cwd);
  const result = useAtomValue(atom);
  const queryData = Option.getOrNull(AsyncResult.value(result));
  const refreshEntries = useAtomCommand(projectEnvironment.refreshEntries, {
    reportFailure: false,
  });
  const [refreshData, setRefreshData] = useState<ProjectListEntriesResult | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshPending, setRefreshPending] = useState(false);
  const queryDataAtRefreshRef = useRef<ProjectListEntriesResult | null>(null);
  const latestQueryDataRef = useRef(queryData);
  const refreshGenerationRef = useRef(0);
  latestQueryDataRef.current = queryData;

  useEffect(() => {
    refreshGenerationRef.current += 1;
    setRefreshData(null);
    setRefreshError(null);
    setRefreshPending(false);
    queryDataAtRefreshRef.current = null;
    return () => {
      refreshGenerationRef.current += 1;
    };
  }, [cwd, environmentId]);

  useEffect(() => {
    if (refreshData !== null && queryData !== queryDataAtRefreshRef.current) {
      setRefreshData(null);
    }
  }, [queryData, refreshData]);

  const refresh = useCallback(() => {
    if (refreshPending) return;
    const generation = refreshGenerationRef.current + 1;
    refreshGenerationRef.current = generation;
    setRefreshPending(true);
    setRefreshError(null);
    void refreshEntries({ environmentId, input: { cwd } }).then((refreshResult) => {
      if (refreshGenerationRef.current !== generation) return;
      setRefreshPending(false);
      if (refreshResult._tag === "Success") {
        queryDataAtRefreshRef.current = latestQueryDataRef.current;
        setRefreshData(refreshResult.value);
        appAtomRegistry.refresh(atom);
        return;
      }
      setRefreshError(failureMessage(squashAtomCommandFailure(refreshResult)));
    });
  }, [atom, cwd, environmentId, refreshEntries, refreshPending]);
  return {
    data: refreshData ?? queryData,
    error: refreshError ?? (refreshData === null ? errorMessage(result) : null),
    isPending: refreshPending || result.waiting,
    refresh,
  };
}

export function useProjectFileQuery(
  environmentId: EnvironmentId,
  cwd: string,
  relativePath: string | null,
): ProjectQueryState<ProjectReadFileResult> & {
  /** The last read from disk, under any unsaved edits; a new object for every completed read. */
  readonly diskRead: ProjectReadFileResult | null;
} {
  const atom = getProjectFileQueryAtom(environmentId, cwd, relativePath);
  const result = useAtomValue(atom);
  const refreshAtom = useAtomRefresh(atom);
  const refresh = useCallback(() => refreshAtom(), [refreshAtom]);
  const data = Option.getOrNull(AsyncResult.value(result));
  const optimisticResult = useAtomValue(
    optimisticFileAtom(environmentId, cwd, relativePath ?? EMPTY_PROJECT_FILE_PATH),
  );
  const optimisticFile = relativePath === null ? null : optimisticResult;

  return {
    data: optimisticFile?.data ?? data,
    diskRead: data,
    error: errorMessage(result),
    isPending: result.waiting,
    refresh,
  };
}
